import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Contact, Lead } from "@lumika/shared";
import {
  CalendarPlus,
  ChevronDown,
  ListTodo,
  MessageSquarePlus,
  Plus,
  Target,
  UserRoundPlus,
} from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@lumika/ui";
import { AppointmentFormDialog } from "./AppointmentFormDialog";
import { ComposeDialog } from "./ComposeDialog";
import { ContactFormDialog } from "./ContactFormDialog";
import { NewLeadDialog } from "./NewLeadDialog";
import { NewTaskDialog } from "./TaskDialogs";

type ConversationTarget = {
  contact: Contact;
  leadId: string;
  rooftopId: string | null;
};

/**
 * Stable global creation menu. A Lead is an opportunity and a Conversation
 * is outreach against one; exposing both labels before anything opens keeps
 * the shell from implying that creating one silently creates the other.
 */
export function NewMenu() {
  const qc = useQueryClient();
  const [conversationOpen, setConversationOpen] = useState(false);
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [newContactOpen, setNewContactOpen] = useState(false);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [newAppointmentOpen, setNewAppointmentOpen] = useState(false);
  const [conversationTarget, setConversationTarget] =
    useState<ConversationTarget>();

  function startConversationForLead(contact: Contact, lead: Lead) {
    setConversationTarget({
      contact,
      leadId: lead.id,
      rooftopId: lead.rooftopId,
    });
    setConversationOpen(true);
  }

  return (
    <>
      {/* This menu opens a dialog; its modal body lock could outlive it and freeze the page. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button size="sm" aria-label="Create new">
            <Plus className="h-4 w-4" />
            <span>New</span>
            <ChevronDown className="h-3.5 w-3.5 opacity-70" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem
            className="min-h-touch items-start py-2"
            onSelect={() => {
              setConversationTarget(undefined);
              setConversationOpen(true);
            }}
          >
            <MessageSquarePlus className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <span className="block font-medium">Start conversation</span>
              <span className="block text-xs text-muted-foreground">
                Message or call a contact
              </span>
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="min-h-touch items-start py-2"
            onSelect={() => setNewLeadOpen(true)}
          >
            <Target className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <span className="block font-medium">New lead</span>
              <span className="block text-xs text-muted-foreground">
                Create an opportunity
              </span>
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="min-h-touch items-start py-2"
            onSelect={() => setNewContactOpen(true)}
          >
            <UserRoundPlus className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <span className="block font-medium">New contact</span>
              <span className="block text-xs text-muted-foreground">
                Add a person without an opportunity
              </span>
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="min-h-touch items-start py-2"
            onSelect={() => setNewTaskOpen(true)}
          >
            <ListTodo className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <span className="block font-medium">New task</span>
              <span className="block text-xs text-muted-foreground">
                Schedule a follow-up or reminder
              </span>
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="min-h-touch items-start py-2"
            onSelect={() => setNewAppointmentOpen(true)}
          >
            <CalendarPlus className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <span className="block font-medium">New appointment</span>
              <span className="block text-xs text-muted-foreground">
                Book a visit at a rooftop
              </span>
            </span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <NewLeadDialog
        open={newLeadOpen}
        onOpenChange={setNewLeadOpen}
        onStartConversation={startConversationForLead}
      />
      <ComposeDialog
        key={conversationTarget?.leadId ?? "new-conversation"}
        presetContact={conversationTarget?.contact}
        targetLeadId={conversationTarget?.leadId}
        targetRooftopId={conversationTarget?.rooftopId}
        open={conversationOpen}
        onOpenChange={(open) => {
          setConversationOpen(open);
          if (!open) setConversationTarget(undefined);
        }}
        trigger="none"
      />
      {/* Both dialogs own their own mount, so they render only while open —
          NewTaskDialog has no `open` prop and starts submitting immediately. */}
      {newContactOpen && (
        <ContactFormDialog
          mode="create"
          contact={null}
          onClose={() => setNewContactOpen(false)}
          onSaved={() => {
            setNewContactOpen(false);
            void qc.invalidateQueries({ queryKey: ["bdc", "contacts"] });
          }}
        />
      )}
      {newTaskOpen && <NewTaskDialog onClose={() => setNewTaskOpen(false)} />}
      {newAppointmentOpen && (
        <AppointmentFormDialog
          mode="create"
          onClose={() => setNewAppointmentOpen(false)}
        />
      )}
    </>
  );
}
